import React, { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MessagePayload } from 'firebase/messaging';
import { getNotificationToken, onMessageListener } from '../services/firebase/firebase';
import { useCustomSnackbar } from '../hooks/useCustomEnqueueSnackbar';
import { firebaseApi } from '../services/apiServices/user/firebase';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { appSlice } from '../store/reducers/AppSlice';
import { GlobalRouteNames } from '../models/Common';
import { ActionTypes } from '../models/PushNotification';
import { currentYear } from '../constants/configuration';
import { getErrorMessage } from '../utils';

interface NotificationProviderProps {
  children: JSX.Element;
  refetchPushes: (_: string) => void;
}

const NotificationProvider: FC<NotificationProviderProps> = ({ children, refetchPushes }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { isAuth } = useAppSelector((state) => state.authReducer);
  const { openPushNotification } = appSlice.actions;
  const [connectDeviceAPI] = firebaseApi.useConnectDeviceMutation();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [firebaseToken, setFirebaseToken] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => {
    getNotificationToken(setFirebaseToken);
  }, []);

  useEffect(() => {
    if (isAuth) refetchPushes('');
  }, [pathname, isAuth]);

  useEffect(() => {
    if (firebaseToken && isAuth) {
      connectDeviceAPI({ deviceID: firebaseToken });
    }
  }, [firebaseToken, isAuth]);

  const handlePushClick = (payload?: MessagePayload) => {
    if (isAuth) refetchPushes('');
    if (payload?.data?.action === ActionTypes.PAYMENTS_HISTORY) {
      if (pathname !== GlobalRouteNames.PaymentHistory) {
        history.push({
          pathname: GlobalRouteNames.PaymentHistory,
        });
      } else {
        history.replace({
          pathname: GlobalRouteNames.PaymentHistory,
          search: `?period=${currentYear}`,
        });
      }
    } else {
      dispatch(openPushNotification(true));
    }
  };

  onMessageListener()
    .then((payload) => {
      if (payload) {
        if (isAuth) refetchPushes('');
        customEnqueueSnackbar(
          payload.notification?.body || '',
          'info',
          payload.notification?.title || '',
          { onClick: () => handlePushClick(payload), autoHideDuration: 10000 },
          payload.notification?.image,
        );
      }
    })
    .catch((err) => {
      const message = getErrorMessage(err);
      return customEnqueueSnackbar(message, 'error');
    });

  return <div>{children}</div>;
};

export default NotificationProvider;
